<template>
  <div>
    <div v-if="!error" class="detail-wrapper">
      <div class="side-wrapper">
        <ul class="list-group list-group-flush">
          <a
            v-for="(col, i) in side_cols"
            :key="i"
            href="#"
            class="list-group-item list-group-item-action flex-column align-items-start side-item"
            v-bind:class="{ selected: i == colIdxSelected }"
            @click="sideColClicked(i)"
          >
            {{ col }}
          </a>
        </ul>
      </div>
      <div v-if="loading">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
      <div class="list-wrapper" v-on:scroll.passive="handleScroll">
        <div
          v-if="json_data"
          class="text-left m-2 font-weight-bold fixed-title"
        >
          <div class="d-flex align-items-center">
            <h4>Protocol: {{ json_data.name }}</h4>
            <PremiumIndicator />
          </div>
        </div>
        <BaseEntry
          v-for="(e, i) in entries"
          :selectedColIdx="colIdxSelected"
          :currIdx="i"
          :key="i"
          :content="e"
          :ref="`entry` + i"
        ></BaseEntry>
      </div>
    </div>
    <div v-else>
      <PageNotFound />
    </div>
  </div>
</template>

<script>
//http://localhost:8082/protocol?key=501683749
//http://localhost:8082/protocol?id=1
import BaseEntry from "@/components/detail/BaseEntry";
import PostsService from "@/services/PostsService";
import PageNotFound from "@/components/common/PageNotFound";
import PremiumIndicator from "@/components/common/PremiumIndicator";
export default {
  name: "ProtocolDetail",
  components: {
    BaseEntry,
    PageNotFound,
  },
  data() {
    return {
      base_url: process.env.VUE_APP_S3_PATH + "protocol/",
      json_data: null,
      side_cols: ["Summary"],
      loading: false,
      error: false,
      colIdxSelected: 0,
      entries: [],
      entryPosys: [],
      helpTexts: {},
    };
  },

  async mounted() {
    if (this.$route.query.key || this.$route.query.id) {
      this.getProtocolDetail();
    } else {
      this.error = true;
      this.loading = false;
    }
  },
  methods: {
    async getProtocolDetail() {
      let protocolId = this.$route.query.key;
      try {
        this.loading = true;
        if (this.$route.query.id) {
          let protocolId2 = this.$route.query.id;
          let response = await PostsService.getProtocolIdById(protocolId2);
          protocolId = response.data;
          if (protocolId == null) {
            this.error = true;
          }
        }
        if (protocolId) {
          let response = await PostsService.getProtocolDetail({
            key: protocolId,
          });
          this.json_data = response.data;

          this.processEntries();
          this.loading = false;
          document.title = `Protocol Detail`;
        }
      } catch (error) {
        console.error("Error fetching protocol details: ", error);
        this.error = true;
        this.loading = false;
      }
    },
    sideColClicked(colIdx) {
      this.scrollToElement(colIdx);
      this.colIdxSelected = colIdx;
    },
    processEntries() {
      this.side_cols.forEach((title) => {
        let entry = {
          title: title,
          name: title.replace(/\s/g, ""),
        };
        switch (title) {
          case "Summary":
            entry.type = "bands";
            entry.bands = this.fillSummary();
            break;
          default:
            entry.type = "bands";
            entry.bands = [];
            break;
        }
        this.entries.push(entry);
      });
    },
    fillSummary() {
      let bands = [];
      bands.push({
        key: "Name",
        text: this.json_data.name,
      });
      //Authos
      bands.push({
        key: "Author(s)",
        text: this.json_data.authors,
        helpTxt: "Name(s) of the individuals who wrote the protocol.",
      });
      bands.push({
        key: "Date Last Modified",
        text: this.dateToYMD(this.json_data.dateLastModified),
      });
      bands.push({
        key: "TAIR Accession",
        text: `Protocol:${this.json_data.tairAccession}`,
      });
      bands.push({
        key: "Description",
        text: `${this.json_data.description}`,
        helpTxt:
          "A brief description of the protocol describing any methods/techniques and the experimental applications of the protocol.",
      });
      //Usage
      bands.push({
        key: "Usage",
        text: `${this.json_data.usage}`,
        helpTxt:
          "User defined keywords which describe the experimental applications or uses for the protocol.",
      });
      //Methods
      bands.push({
        key: "Methods",
        text: `${this.json_data.methods}`,
      });
      //Publication
      bands.push({
        key: "Publication",
        text: this.json_data.publication
          ? this.json_data.publication.title
          : "N/A",
        type: "link",
        helpTxt:
          "For protocols described elsewhere in a publiction such as an article or book chapter, this points to the corresponding publication. Click on the link to view the detailed record for the publication.",
        link: this.json_data.publication
          ? `/publication?key=${this.json_data.publication.referenceId}`
          : "",
      });
      //Keywords
      bands.push({
        key: "Associated Keywords",
        type: "links",
        links: this.json_data.keywords
          ? this.json_data.keywords.map((k) => {
              return {
                name: k.keyword,
                link: `/keyword?key=${k.keywordId}`,
              };
            })
          : [],
        helpTxt: "The controlled vocabulary term.",
      });
      //URLs
      bands.push({
        key: "Download/View",
        type: "links",
        links: this.json_data.websites
          ? this.json_data.websites.map((u) => {
              return {
                name: u.webSiteName,
                link: u.url,
              };
            })
          : [],
      });
      return bands;
    },

    //Getters
    dateToYMD(dateTs) {
      let date = new Date(dateTs);
      const d = date.getDate();
      const m = date.getMonth() + 1;
      const y = date.getFullYear();
      return `${y}-${m.toString().padStart(2, "0")}-${d
        .toString()
        .padStart(2, "0")}`;
    },
    //
    scrollToElement(colIdx) {
      const entryRef = this.$refs["entry" + colIdx];
      const el = entryRef[0].$el;
      if (el) {
        const top = el.offsetTop - 210;
        el.parentElement.scrollTo(0, top);
      }
    },
    handleScroll(e) {
      const scrollTop = e.target.scrollTop + 215;
      let currTopEntry = 0;
      this.entryPosys.forEach((e, i) => {
        if (scrollTop > e) {
          currTopEntry = i;
        }
      });
      this.colIdxSelected = currTopEntry;
    },
  },
};
</script>

<style scoped lang="scss">
.fixed-title {
  position: sticky;
  top: 0;
  background-color: #f1efec;
  z-index: 10;
  padding-left: 10px;
  padding-top: 10px;
}

.my-custom-scrollbar {
  position: relative;
  height: 200px;
  overflow: auto;
}
.table-wrapper-scroll-y {
  display: block;
}
</style>
